import React, { memo, useImperativeHandle, useRef, forwardRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { MultiSelectWithCheckboxes as BaseMultiSelectWithCheckboxes } from '../Controls'
import { handleRequired, ErrorMessage, useFormBase, formImperativeHandleFunctions, handleFormPostObject } from './common'
import { translate } from '../../multilanguage'
import { FormFieldContainer } from './constants'
import { useTranslation } from 'react-i18next'
/**
 * Implements a multi MUI Select with the items as checkboxes for forms.
 *
 * @param {object} props component props
 * @returns {MultiSelectWithCheckboxes} MultiSelectWithCheckboxes component
 */
const MultiSelectWithCheckboxes = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fieldTemplate, data, items, label, onClose, ...finalProps } = props
  const [selectItems, setSelectItems] = useState(items)
  const originalItems = useRef(items)

  useEffect(() => {
    if (items.length > 0) {
      originalItems.current = [...items]
    }
    setSelectItems(items)
  }, [items])

  const { onChange, errorMsgFixedHeight, disabled, setDisabled, fieldLabel, refFormElement, valueRef, errorMsg, setErrorMsg } = 
    useFormBase(props, fieldTemplate, data)

  useImperativeHandle(ref, () => {
    const funcsObj = formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled)
    return {
      ...funcsObj,
      clear: () => {
        funcsObj.clear()
        setSelectItems(originalItems.current)
      },
      setSelectItems: items => setSelectItems(items),
      getItems: () => selectItems,
      validate: (postObject, refs) => {
        const value = valueRef.current
        let error = ''

        if (fieldTemplate.isRequired && value.length === 0) {
          error = handleRequired(props)
        } else if (fieldTemplate.validator) {
          error = translate(fieldTemplate.validator(value))
        }

        return handleFormPostObject(postObject, fieldTemplate, value, refs, error, setErrorMsg)
      },
    }
  })

  return (
    <FormFieldContainer>
      <BaseMultiSelectWithCheckboxes
        {...finalProps}
        items={selectItems}
        disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
        ref={refFormElement}
        onChange={onChange}
        onClose={onClose}
        error={!!errorMsg}
        label={t(`${fieldLabel.current}`)}
        noTranslate={true}
      />
      <ErrorMessage errorMsg={errorMsg} errorMsgFixedHeight={errorMsgFixedHeight} />
    </FormFieldContainer>
  )
}))

MultiSelectWithCheckboxes.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Initial value for the select.
   */
  defaultValue: PropTypes.array,
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the Select is clicked.
   */
  onChange: PropTypes.func,
  /**
   onClose function called when the Select is closed.
   */
  onClose: PropTypes.func,
  /**
   Items to display in the radio group.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the items labels.
   */
  noTranslateItems: PropTypes.bool,
  /**
   Style for the control.
   */
  style: PropTypes.object,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['outlined', 'standard']),
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Puts the control in an error state.
   */
  error: PropTypes.bool,
  /**
   MUI color for the checkboxes in the select.
   */
  checkboxColor: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Set the select items.
   */
  'ref.current.setSelectItems': PropTypes.func,
  /**
   Get the select items.
   */
  'ref.current.getItems': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

MultiSelectWithCheckboxes.defaultProps = {
  noTranslate: false,
  noTranslateItems: false,
}

export default MultiSelectWithCheckboxes
